import { useState, useEffect, useCallback } from 'react';
import { read } from 'xlsx';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, Info, Eye } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { HedgePosition, MarginData } from '@/lib/hedgeAnalysis';

interface Props {
  onImportComplete: (positions: HedgePosition[], marginUsed: number, marginData?: MarginData) => void;
}

interface PreviewRow {
  row: number;
  name: string;
  quantity: number;
  value: number;
  currency: string;
}

// Live exchange rates (fallback included)
async function fetchRates(): Promise<Record<string, number>> {
  try {
    const res = await fetch('https://api.frankfurter.app/latest?from=CHF&to=USD,EUR,GBP,SEK,NOK,DKK,JPY,CAD,AUD,HKD,SGD');
    const data = await res.json();
    const rates: Record<string, number> = { CHF: 1 };
    for (const [cur, val] of Object.entries(data.rates)) {
      rates[cur] = 1 / (val as number);
    }
    return rates;
  } catch {
    return { CHF: 1, USD: 0.88, EUR: 0.95, GBP: 1.12, SEK: 0.083, NOK: 0.082, DKK: 0.127, JPY: 0.0059, CAD: 0.65, AUD: 0.57, HKD: 0.113, SGD: 0.66 };
  }
}

function detectCurrency(value: string): string {
  const v = (value || '').toUpperCase().trim();
  if (v.includes('USD') || v.startsWith('$')) return 'USD';
  if (v.includes('EUR') || v.startsWith('€')) return 'EUR';
  if (v.includes('GBP') || v.startsWith('£')) return 'GBP';
  if (v.includes('SEK')) return 'SEK';
  if (v.includes('NOK')) return 'NOK';
  if (v.includes('DKK')) return 'DKK';
  if (v.includes('JPY') || v.startsWith('¥')) return 'JPY';
  if (v.includes('CAD')) return 'CAD';
  if (v.includes('AUD')) return 'AUD';
  if (v.includes('HKD')) return 'HKD';
  if (v.includes('SGD')) return 'SGD';
  if (v.includes('CHF')) return 'CHF';
  return 'CHF';
}

function parseNum(v: any): number {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  const s = String(v).replace(/[^0-9.\-]/g, '');
  return parseFloat(s) || 0;
}

export function HedgeExcelImport({ onImportComplete }: Props) {
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [startLine, setStartLine] = useState(15);
  const [endLine, setEndLine] = useState(157);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<PreviewRow[] | null>(null);
  const [previewSheet, setPreviewSheet] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRates, setPreviewRates] = useState<Record<string, number> | null>(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importStep, setImportStep] = useState('');
  const { toast } = useToast();

  const acceptFile = (file: File) => {
    if (file && /\.(xlsx|xls|xlsm)$/i.test(file.name)) {
      setSelectedFile(file);
      setPreview(null);
    } else {
      toast({ title: 'Invalid file', description: 'Please use an .xlsx, .xls, or .xlsm file', variant: 'destructive' });
    }
  };

  const generatePreview = useCallback(async () => {
    if (!selectedFile) return;
    setPreviewLoading(true);
    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = read(data);
      const sheetName = workbook.SheetNames.find(n => n.toUpperCase().includes('PRVFIN'));
      if (!sheetName) {
        toast({ title: 'Sheet not found', description: `"PRVFIN" not found. Available: ${workbook.SheetNames.join(', ')}`, variant: 'destructive' });
        setPreviewLoading(false);
        return;
      }
      setPreviewSheet(sheetName);
      const ws = workbook.Sheets[sheetName];
      const cell = (addr: string) => ws?.[addr]?.v;

      const rows: PreviewRow[] = [];
      for (let r = startLine; r <= endLine; r++) {
        const name = String(cell(`C${r}`) || '').trim();
        if (!name) continue;
        const quantity = parseNum(cell(`G${r}`));
        if (quantity <= 0) continue;
        const value = parseNum(cell(`J${r}`));
        if (value <= 0) continue;

        let currency = 'CHF';
        const kVal = String(cell(`K${r}`) || '');
        const iVal = String(cell(`I${r}`) || '');
        const hVal = String(cell(`H${r}`) || '');
        if (kVal) currency = detectCurrency(kVal);
        else if (iVal) currency = detectCurrency(iVal);
        else if (hVal) currency = detectCurrency(hVal);

        rows.push({ row: r, name, quantity, value, currency });
      }
      setPreview(rows);
      if (rows.length > 0) {
        const rates = await fetchRates();
        setPreviewRates(rates);
      }
      if (rows.length === 0) {
        toast({ title: 'No data found', description: `No valid positions in rows ${startLine}-${endLine}`, variant: 'destructive' });
      }
    } catch (err: any) {
      toast({ title: 'Preview failed', description: err.message, variant: 'destructive' });
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedFile, startLine, endLine, toast]);

  useEffect(() => {
    if (selectedFile) {
      generatePreview();
    }
  }, [selectedFile, startLine, endLine, generatePreview]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) acceptFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) acceptFile(file);
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setLoading(true);
    setImportProgress(0);
    setImportStep('Reading Excel file...');

    try {
      setImportProgress(10);
      const data = await selectedFile.arrayBuffer();
      const workbook = read(data);

      setImportStep('Locating PRVFIN sheet...');
      setImportProgress(20);

      const sheetName = workbook.SheetNames.find(n => n.toUpperCase().includes('PRVFIN'));
      if (!sheetName) {
        throw new Error(`Sheet "PRVFIN" not found. Available sheets: ${workbook.SheetNames.join(', ')}`);
      }
      const ws = workbook.Sheets[sheetName];
      const cell = (addr: string) => ws?.[addr]?.v;

      const marginRaw = parseNum(cell('M275'));
      const m163Raw = parseNum(cell('M163'));
      const marginRatio = m163Raw > 0 ? marginRaw / m163Raw : 0;
      const marginUsed = marginRatio > 0 ? Math.round(marginRatio * 10000) / 100 : 0; // margin ratio as %
      const marginData: MarginData = {
        marginRaw,
        portfolioValue: m163Raw,
        marginRatio,
        marginRatioPct: marginRatio * 100,
      };

      setImportStep('Fetching live exchange rates...');
      setImportProgress(30);
      const rates = await fetchRates();

      setImportStep('Parsing positions...');
      setImportProgress(40);

      const positions: HedgePosition[] = [];
      const seen = new Set<string>();
      const currencySummary: Record<string, number> = {};
      const totalRows = endLine - startLine + 1;

      for (let r = startLine; r <= endLine; r++) {
        // Update progress proportionally through parsing
        const parseProgress = 40 + Math.round(((r - startLine) / totalRows) * 40);
        if (r % 10 === 0) {
          setImportProgress(parseProgress);
          setImportStep(`Parsing position ${r - startLine + 1} of ${totalRows}...`);
        }

        const stockName = String(cell(`C${r}`) || '').trim();
        if (!stockName) continue;

        const quantity = parseNum(cell(`G${r}`));
        if (quantity <= 0) continue;

        const totalValueLocal = parseNum(cell(`J${r}`));
        if (totalValueLocal <= 0) continue;

        let localCurrency = 'CHF';
        const kVal = String(cell(`K${r}`) || '');
        const iVal = String(cell(`I${r}`) || '');
        const hVal = String(cell(`H${r}`) || '');
        if (kVal) localCurrency = detectCurrency(kVal);
        else if (iVal) localCurrency = detectCurrency(iVal);
        else if (hVal) localCurrency = detectCurrency(hVal);

        const currentPrice = totalValueLocal / quantity;

        const ticker = stockName
          .replace(/\s+(SA|AG|PLC|INC|CORP|LTD|NV|SE|SPA|AB|OYJ|ASA)\.?$/i, '')
          .split(/\s+/)
          .slice(0, 2)
          .join(' ')
          .toUpperCase();

        if (seen.has(ticker)) continue;
        seen.add(ticker);

        const rateToChf = rates[localCurrency] || 1;
        const chfToUsd = 1 / (rates['USD'] || 0.88);
        const totalValueUSD = totalValueLocal * rateToChf * chfToUsd;
        const currentPriceForAnalysis = currentPrice;

        currencySummary[localCurrency] = (currencySummary[localCurrency] || 0) + totalValueLocal * rateToChf;

        const buyPrice = parseNum(cell(`E${r}`)) || parseNum(cell(`F${r}`));
        const gainPct = buyPrice > 0 ? (currentPrice - buyPrice) / buyPrice : 0;
        const initialInvestment = buyPrice > 0 ? buyPrice * quantity : totalValueLocal;

        // BR column = column 70 in Excel = "BR"
        const brVal = parseNum(cell(`BR${r}`));

        positions.push({
          ticker,
          stockName,
          quantity,
          currentPrice: currentPriceForAnalysis,
          totalValue: totalValueUSD,
          localCurrency,
          gainPct,
          initialInvestment: initialInvestment * rateToChf * chfToUsd,
          brPerformance: brVal,
        });
      }

      setImportStep('Converting currencies...');
      setImportProgress(85);

      if (positions.length === 0) {
        throw new Error(`No valid positions found in rows ${startLine}-${endLine} of sheet "${sheetName}". Check that Col D has names, Col G has quantities, and Col J has values.`);
      }

      setImportStep('Finalising import...');
      setImportProgress(95);

      const currencyBreakdown = Object.entries(currencySummary)
        .sort(([, a], [, b]) => b - a)
        .map(([cur, chf]) => `${cur}: CHF ${Math.round(chf).toLocaleString()}`)
        .join(' | ');

      setImportProgress(100);
      setImportStep(`${positions.length} positions imported — starting AI analysis...`);

      toast({
        title: 'Portfolio Imported from PRVFIN',
        description: `${positions.length} positions loaded. Margin ratio: ${(marginRatio * 100).toFixed(2)}% (M275: CHF ${Math.round(marginRaw).toLocaleString()} / M163: CHF ${Math.round(m163Raw).toLocaleString()})`,
      });

      onImportComplete(positions, marginUsed, marginData);
    } catch (error: any) {
      toast({
        title: 'Import Failed',
        description: error.message || 'Failed to parse Excel file',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="hedge-start-line">Start Line (row)</Label>
          <Input id="hedge-start-line" type="number" min="1" value={startLine} onChange={(e) => setStartLine(Number(e.target.value))} disabled={loading} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="hedge-end-line">End Line (row)</Label>
          <Input id="hedge-end-line" type="number" min="1" value={endLine} onChange={(e) => setEndLine(Number(e.target.value))} disabled={loading} />
        </div>
      </div>

      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border'}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <label htmlFor="hedge-excel-upload" className="cursor-pointer">
          <span className="text-sm text-muted-foreground">
            {selectedFile ? selectedFile.name : 'Drag & drop or click to select your portfolio Excel file (PRVFIN tab)'}
          </span>
          <input id="hedge-excel-upload" type="file" accept=".xlsx,.xls,.xlsm" className="hidden" onChange={handleFileSelect} disabled={loading} />
        </label>
      </div>

      {/* Import Progress Bar */}
      {loading && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">{importStep}</p>
            <span className="text-sm font-mono text-muted-foreground">{importProgress}%</span>
          </div>
          <Progress value={importProgress} className="h-2" />
        </div>
      )}

      {/* Preview section */}
      {previewLoading && (
        <div className="text-center text-sm text-muted-foreground py-4">Scanning file...</div>
      )}

      {preview && preview.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              Preview: {preview.length} positions found in "{previewSheet}" (rows {startLine}–{endLine})
            </span>
          </div>
          <div className="max-h-60 overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14">Row</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="w-16">Ccy</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.map((p) => (
                  <TableRow key={p.row}>
                    <TableCell className="text-muted-foreground text-xs">{p.row}</TableCell>
                    <TableCell className="text-xs font-medium truncate max-w-[200px]">{p.name}</TableCell>
                    <TableCell className="text-right text-xs">{p.quantity.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-xs">{p.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell className="text-xs">{p.currency}</TableCell>
                  </TableRow>
                ))}
                {(() => {
                  const byCurrency: Record<string, number> = {};
                  preview.forEach(p => { byCurrency[p.currency] = (byCurrency[p.currency] || 0) + p.value; });
                  const currencyRows = Object.entries(byCurrency).sort(([,a],[,b]) => b - a);
                  const grandTotalCHF = previewRates
                    ? currencyRows.reduce((sum, [ccy, total]) => sum + total * (previewRates[ccy] || 1), 0)
                    : null;
                  return (
                    <>
                      {currencyRows.map(([ccy, total]) => {
                        const rate = previewRates?.[ccy] || 1;
                        const chfValue = total * rate;
                        return (
                          <TableRow key={`total-${ccy}`} className="bg-muted/50 font-semibold border-t">
                            <TableCell className="text-xs"></TableCell>
                            <TableCell className="text-xs">
                              Total ({ccy})
                              {previewRates && ccy !== 'CHF' && (
                                <span className="ml-1 font-normal text-muted-foreground">
                                  × {rate.toFixed(4)} = CHF {Math.round(chfValue).toLocaleString()}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              {preview.filter(p => p.currency === ccy).reduce((s, p) => s + p.quantity, 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right text-xs">{total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                            <TableCell className="text-xs">{ccy}</TableCell>
                          </TableRow>
                        );
                      })}
                      {grandTotalCHF !== null && (
                        <TableRow className="bg-primary/10 font-bold border-t-2">
                          <TableCell className="text-xs"></TableCell>
                          <TableCell className="text-xs">Grand Total</TableCell>
                          <TableCell className="text-right text-xs"></TableCell>
                          <TableCell className="text-right text-xs">{Math.round(grandTotalCHF).toLocaleString()}</TableCell>
                          <TableCell className="text-xs font-bold">CHF</TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })()}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Button onClick={handleImport} disabled={loading || !selectedFile} className="w-full">
        {loading ? 'Importing & converting currencies...' : 'Import & Analyse with 4 AI Models'}
      </Button>

      <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg">
        <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground">
          <strong>PRVFIN tab mapping:</strong> Col C = Stock name, Col G = Quantity, Col J = Value (local currency), 
          Col K/I/H = Currency detection, M275 = Margin (CHF). 
          All values are auto-converted to USD using live exchange rates for unified analysis.
        </p>
      </div>
    </div>
  );
}
